import React, { useState } from 'react';
import { AppHeader } from '../../components/AppHeader/AppHeader';
import { useShellNav } from '../../access/useShellNav';
import {
  Icon,
  MenuLeaf,
  Sidebar,
  Submenu,
} from 'barte-design-system';
import { ChartLineUpIcon, FlaskIcon } from '@phosphor-icons/react';
import './SellerLayout.css';

export type SellerActiveItem =
  | 'v1'
  | 'v2'
  | 'v3'
  | 'v31'
  | 'estado-padrao'
  | 'sem-banking'
  | 'sem-gravames-bolsao'
  | 'disponivel-futuro-zerados'
  | 'sem-saque'
  | 'sellers-timeline'
  | 'primeira-entrega';

interface SellerLayoutProps {
  activeItem?: SellerActiveItem;
  children: React.ReactNode;
}

const TEST_ITEMS: SellerActiveItem[] = ['v1', 'v2', 'v3', 'v31'];
const PROD_ITEMS: SellerActiveItem[] = [
  'primeira-entrega',
  'estado-padrao',
  'sem-banking',
  'sem-gravames-bolsao',
  'disponivel-futuro-zerados',
  'sem-saque',
  'sellers-timeline',
];

type GroupKey = 'testes' | 'producao';

export function SellerLayout({ activeItem, children }: SellerLayoutProps) {
  const { sidebarProps, navigate } = useShellNav();

  const isTestActive = !!activeItem && TEST_ITEMS.includes(activeItem);
  const isProdActive = !!activeItem && PROD_ITEMS.includes(activeItem);

  // Estado controlado de abertura dos grupos.
  // Inicialmente abre o grupo do item ativo.
  const initialOpenGroups = new Set<GroupKey>();
  if (isTestActive) initialOpenGroups.add('testes');
  if (isProdActive) initialOpenGroups.add('producao');

  const [openGroups, setOpenGroups] = useState<Set<GroupKey>>(initialOpenGroups);

  const toggleGroup = (key: GroupKey) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="app-layout">
      <AppHeader variant="seller" avatarProps={{ variant: 'text', initials: 'M', color: 'brand' }} />

      <div className="app-body">
        <Sidebar {...sidebarProps}>
          {/* Grupo: Dash Saldos [testes] */}
          <MenuLeaf
            label="Dash Saldos [testes]"
            icon={<Icon icon={FlaskIcon} size={20} weight="regular" />}
            isOpen={openGroups.has('testes')}
            isActive={isTestActive}
            onClick={() => toggleGroup('testes')}
          >
            <Submenu
              label="V1"
              isActive={activeItem === 'v1'}
              onClick={() => navigate('/portal-sellers/dashboard-saldos')}
            />
            <Submenu
              label="V2"
              isActive={activeItem === 'v2'}
              onClick={() => navigate('/portal-sellers/dashboard-saldos-v2')}
            />
            <Submenu
              label="V3"
              isActive={activeItem === 'v3'}
              onClick={() => navigate('/portal-sellers/dashboard-saldos-v3')}
            />
            <Submenu
              label="V3.1"
              isActive={activeItem === 'v31'}
              onClick={() => navigate('/portal-sellers/dashboard-saldos-v3-1')}
            />
          </MenuLeaf>

          {/* Grupo: Dash Saldos */}
          <MenuLeaf
            label="Dash Saldos"
            icon={<Icon icon={ChartLineUpIcon} size={20} weight="regular" />}
            isOpen={openGroups.has('producao')}
            isActive={isProdActive}
            onClick={() => toggleGroup('producao')}
          >
            <Submenu
              label="Primeira entrega"
              isActive={activeItem === 'primeira-entrega'}
              onClick={() => navigate('/portal-sellers/primeira-entrega')}
            />
            <Submenu
              label="Estado padrão"
              isActive={activeItem === 'estado-padrao'}
              onClick={() => navigate('/portal-sellers/dashboard-saldos-v4')}
            />
            <Submenu
              label="Sem banking"
              isActive={activeItem === 'sem-banking'}
              onClick={() => navigate('/portal-sellers/dashboard-saldos-sem-banking')}
            />
            <Submenu
              label="Sem Gravames e Bolsão"
              isActive={activeItem === 'sem-gravames-bolsao'}
              onClick={() => navigate('/portal-sellers/dashboard-saldos-sem-gravames-bolsao')}
            />
            <Submenu
              label="Disponível e futuro zerados"
              isActive={activeItem === 'disponivel-futuro-zerados'}
              onClick={() => navigate('/portal-sellers/dashboard-saldos-disponivel-futuro-zerados')}
            />
            <Submenu
              label="Sem saque"
              isActive={activeItem === 'sem-saque'}
              onClick={() => navigate('/portal-sellers/dashboard-saldos-sem-saque')}
            />
            <Submenu
              label="Sellers Timeline"
              isActive={activeItem === 'sellers-timeline'}
              onClick={() => navigate('/portal-sellers/dashboard-saldos-sellers-timeline')}
            />
          </MenuLeaf>
        </Sidebar>

        {children}
      </div>
    </div>
  );
}
